import { useMutation } from 'react-query';
import { HStack, View, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Button, ControlInput, FormHelperText, Text } from '@pimm/base';
import { ChangePinRequest, PinResetDto } from '@pimm/services/lib/sms-tenants';
import { ChangePin } from '@pimm/services/lib/sms-tenants/services';
import { useFormPin } from '../hooks';

type FormPinProps = {
  isPinNotSet?: boolean;
  PinHeaderComponent?: React.ReactNode;
  userId: string;
  onCancel?: () => void;
  onPressYes?: () => void;
  onEndChange: (payload?: PinResetDto) => void;
};

export const FormPinSetup = ({ PinHeaderComponent, isPinNotSet, userId, ...props }: FormPinProps) => {
  const form = useFormPin();
  const { t } = useTranslation();
  const errMessage = form.formState.errors.root?.message as string;

  const changePin = useMutation({
    mutationFn: ChangePin,
    onSuccess: data => props.onEndChange(data),
    onError: (error: any) => {
      // Reset input fields but keep the error message
      form.reset(undefined, { keepErrors: true });

      form.setError('root', {
        type: 'custom',
        message: (error.code ?? error.status) === 400 ? 'account_settings:invalid_password' : 'account_settings:error_try_again',
      });
    },
  });

  const handleSavePin = () => {
    const { currentPassword, pin, confirmPin } = form.getValues();

    if (pin !== confirmPin) {
      form.setError('confirmPin', { type: 'custom', message: t('account_settings:pin_mismatch') });
      return;
    }

    const payload: ChangePinRequest = {
      userId: userId,
      currentPassword: btoa(currentPassword),
      newPin: pin,
    };

    changePin.mutate(payload);
  };

  return (
    <VStack height="full" w="full" p={6}>
      {PinHeaderComponent && <View>{PinHeaderComponent}</View>}

      {!isPinNotSet && (
        <VStack space={4} justifyContent="center" pb={4}>
          <VStack alignItems="flex-start" justifyContent="center">
            <Text size="md" fontWeight={500} color="gray.900">
              {t('account_settings:current_password')}
            </Text>
            <HStack space={2} alignItems="center">
              <ControlInput
                control={form.control}
                type="password"
                name="currentPassword"
                _helperText={{ textAlign: 'left' }}
                _input={{ w: 300 }}
                renderErrorText={_err => t(_err ?? '')}
              />
            </HStack>
          </VStack>

          <VStack alignItems="flex-start" justifyContent="center">
            <Text size="md" fontWeight={500} color="gray.900">
              {t('account_settings:new_pin')}
            </Text>
            <HStack space={2} alignItems="center">
              <ControlInput
                numericOnly
                control={form.control}
                type="password"
                name="pin"
                _helperText={{ textAlign: 'left' }}
                _input={{ w: 300, maxLength: 4 }}
                renderErrorText={_err => t(_err ?? '')}
              />
            </HStack>
          </VStack>

          <VStack alignItems="flex-start" justifyContent="center">
            <Text size="md" fontWeight={500} color="gray.900">
              {t('account_settings:confirm_pin')}
            </Text>
            <HStack space={2} alignItems="center">
              <ControlInput
                numericOnly
                control={form.control}
                type="password"
                name="confirmPin"
                _helperText={{ textAlign: 'left' }}
                _input={{ w: 300, maxLength: 4 }}
                renderErrorText={_err => t(_err ?? '')}
              />
            </HStack>
            <Text color="gray.600" fontWeight={500} size="md" lineHeight="md" italic>
              {t('account_settings:pin_4_digits_required')}
            </Text>
          </VStack>

          <FormHelperText error visible={!!errMessage} textAlign="center">
            {t(errMessage) ?? ''}
          </FormHelperText>
        </VStack>
      )}

      <HStack space={3} justifyContent="flex-end" width="full">
        <Button variant="unstyled" flex={1} disabled={changePin.isLoading} onPress={props.onCancel}>
          {!!isPinNotSet ? t('account_settings:no') : t('account_settings:cancel')}
        </Button>

        <Button
          flex={1}
          disabled={!form.formState.isDirty && !isPinNotSet}
          isLoading={changePin.isLoading}
          onPress={!isPinNotSet ? form.handleSubmit(handleSavePin) : props.onPressYes && props.onPressYes}
        >
          {!!isPinNotSet ? t('account_settings:yes') : t('account_settings:save')}
        </Button>
      </HStack>
    </VStack>
  );
};
